import { useMemo } from 'react';
import Image from 'next/image';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';

import { EmployerActivityCard, SkeletonEmployerCardLoading, Title, ButtonV2 } from '@restworld/ui-ds';
import { GetMissedOrgApplicationActivities } from '@restworld/data-services';
import { getAssetSrc, customImageLoader } from '@restworld/utils-common';

import { infiniteActivitiesQueryResponseAtom } from './hooks/use-missed-organisation-application-activities-query';

function ActivityList() {
  const { t } = useTranslation('dashboard-page');
  const { activities, hasNextPage, query } = useAtomValue(infiniteActivitiesQueryResponseAtom);
  const isEmpty = useMemo(() => !activities || activities?.length === 0, [activities]);
  if (query.isLoading) {
    return <SkeletonEmployerCardLoading />;
  }

  if (isEmpty) {
    return <EmptyActivityList />;
  }

  return (
    <SectionHighlightWrapper>
      <ActivityListWrapper>
        {activities.map((activity, i) => (
          <ActivityItem key={i} activity={activity} isFirst={i === 0} isLast={i === activities.length - 1} />
        ))}
        {hasNextPage && (
          <div tw="flex justify-center pt-16">
            <ButtonV2
              StartIcon={PlusIcon}
              variant="tertiary"
              onClick={() => query.fetchNextPage()}
              loading={query.isFetchingNextPage}
            >
              {t('load_more_items')}
            </ButtonV2>
          </div>
        )}
      </ActivityListWrapper>
    </SectionHighlightWrapper>
  );
}
export default ActivityList;

function ActivityItem({
  activity: {
    applicant_avatars,
    application_count,
    category,
    city,
    occupation_name,
    restaurant_name,
    updated_at,
    job_position_id,
    restaurant_id,
  },
  isFirst,
  isLast,
}: ActivityItemProps) {
  const candidates = useMemo(
    () =>
      applicant_avatars?.map((candidate) => ({
        firstName: candidate.name,
        lastName: candidate.surname,
        image: candidate.avatar_url,
      })),
    [applicant_avatars]
  );
  const href = useMemo(
    () =>
      category === 'spontaneous_applications'
        ? `/employer/spontaneous-applications?restaurant_id=${restaurant_id}`
        : `/employer/job-positions/${job_position_id}`,
    [category, job_position_id, restaurant_id]
  );

  return (
    <EmployerActivityCard
      href={href}
      variant={category}
      candidateCount={application_count}
      city={city}
      jobPositionTitle={occupation_name}
      restaurantName={restaurant_name}
      dateSinceLastActivity={updated_at}
      candidates={candidates}
      isFirst={isFirst}
      isLast={isLast}
    />
  );
}

function EmptyActivityList() {
  const { t } = useTranslation('dashboard-page');

  return (
    <SectionHighlightWrapper>
      <EmptyListWrapper>
        <Title
          fontSize="base"
          fontWeight="medium"
          textColor="neutral-200"
          twStyle={tw`w-full md:(max-w-[600px]) mb-2 mx-auto text-center`}
        >
          {t('empty_activities_message')}
        </Title>
        <ImageWrapper>
          <Image fill src={getAssetSrc('/utils-assets/person-with-trophy.svg')} alt="" loader={customImageLoader} />
        </ImageWrapper>
      </EmptyListWrapper>
    </SectionHighlightWrapper>
  );
}

interface ActivityItemProps {
  activity: GetMissedOrgApplicationActivities;
  isLast?: boolean;
  isFirst?: boolean;
}

const ActivityListWrapper = styled.div`
  ${tw`border border-purple-700 rounded-lg`}
`;
const EmptyListWrapper = styled.div`
  ${tw`p-10 border border-purple-700 rounded-lg bg-neutral-10`}
`;
const ImageWrapper = styled.div`
  ${tw`w-full sm:w-[126px] h-[150px] relative mx-auto`}
`;
const SectionHighlightWrapper = styled.div`
  ${tw`bg-purple-50 rounded-2xl p-2`}
`;
